import { onMounted, onBeforeUnmount, nextTick } from 'vue';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

const isVisible = el => el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;

/**
 * Trap keyboard focus inside a dialog for the component's lifetime.
 *
 * On mount: remembers the trigger, moves focus into the dialog. While mounted:
 * wraps Tab/Shift+Tab at the boundaries and pulls focus back if it escapes.
 * On unmount: restores focus to the trigger.
 *
 * @param {import('vue').Ref<HTMLElement|null>} rootRef Ref to the dialog root.
 * @param {Object} [options]
 * @param {import('vue').Ref<HTMLElement|null>} [options.initialFocusRef] Element to
 *   receive initial focus instead of the first focusable item. Only honored when it
 *   is focusable at mount time; otherwise falls back to the default behavior.
 * @param {boolean} [options.moveFocusOnMount=true] Set false to leave the caret where
 *   it is. Tab still pulls focus into the trap on first press.
 */
export function useFocusTrap(rootRef, { initialFocusRef, moveFocusOnMount = true } = {}) {
  let previouslyFocused = null;

  // Recomputed on every Tab so dynamically-added controls are always included.
  const focusableItems = () => {
    const root = rootRef.value;
    if (!root) return [];
    return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
  };

  const handleKeydown = event => {
    if (event.key !== 'Tab') return;

    const root = rootRef.value;
    if (!root) return;

    const items = focusableItems();
    if (items.length === 0) {
      event.preventDefault();
      root.focus();
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const inside = root.contains(active);

    if (event.shiftKey) {
      if (active === first || !inside) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !inside) {
      event.preventDefault();
      first.focus();
    }
  };

  onMounted(() => {
    previouslyFocused = document.activeElement;
    // Capture phase so we wrap before any inner Tab handlers act.
    document.addEventListener('keydown', handleKeydown, true);

    if (!moveFocusOnMount) return;

    nextTick(() => {
      const initial = initialFocusRef?.value;
      const focusable =
        initial && (initial === rootRef.value || initial.matches(FOCUSABLE_SELECTOR));
      if (focusable && isVisible(initial)) {
        initial.focus();
        return;
      }

      const [firstItem] = focusableItems();
      (firstItem || rootRef.value)?.focus();
    });
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown, true);

    if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
      previouslyFocused.focus();
    }
  });
}
